/// <amd-module name="Build/OrgSettings/OrgSettingsService" />
import { PipelineTriggerSettings } from "Build/Client/Contracts/Build";
import { OrgSettingsData } from "Build/Client/Contracts/DataProvider";
import { ContributionSource } from "Build/Flux/Sources/Contribution";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IGeneralSettingsStoreOptions {
    pageContext: IVssPageContext;
}

export const DisabledSettings: PipelineTriggerSettings = {
    forkProtectionEnabled: true,
    buildsEnabledForForks: false,
    enforceJobAuthScopeForForks: false,
    enforceNoAccessToSecretsFromForks: false,
    isCommentRequiredForPullRequest: false,
    requireCommentsForNonTeamMembersOnly: false,
    requireCommentsForNonTeamMemberAndNonContributors: false
};

export const BestPracticeSettings: PipelineTriggerSettings = {
    forkProtectionEnabled: true,
    buildsEnabledForForks: true,
    enforceJobAuthScopeForForks: true,
    enforceNoAccessToSecretsFromForks: true,
    isCommentRequiredForPullRequest: true,
    requireCommentsForNonTeamMembersOnly: false,
    requireCommentsForNonTeamMemberAndNonContributors: false
};

export interface IOrgSettingsService extends IVssService {
    data: IReadonlyObservableValue<OrgSettingsData>;
    hasNoPermission: IReadonlyObservableValue<boolean>;
    updateBadgesArePrivate: (newVal: boolean) => void;
    updateEnforceSettableVar: (newVal: boolean) => void;
    updateAuditEnforceSettableVar: (newVal: boolean) => void;
    updateJobAuthScope: (newVal: boolean) => void;
    updateJobAuthScopeForReleases: (newVal: boolean) => void;
    updateDisableInBoxTasksVar: (newVal: boolean) => void;
    updateDisableMarketplaceTasksVar: (newVal: boolean) => void;
    updateDisableNode6TasksVar: (newVal: boolean) => void;
    updateEnforceReferencedRepoScopedToken: (newVal: boolean) => void;
    updateDisableStageChooser: (newVal: boolean) => void;
    updateDisableClassicPipelineCreation: (newVal: boolean) => void;
    updateDisableClassicBuildPipelineCreation: (newVal: boolean) => void;
    updateDisableClassicReleasePipelineCreation: (newVal: boolean) => void;
    updatePipelineTriggerSettings: (settings : {buildsEnabledForForks?: boolean,
        enforceJobAuthScopeForForks?: boolean,
        enforceNoAccessToSecretsFromForks?: boolean,
        forkProtectionEnabled?: boolean,
        isCommentRequiredForPullRequest?: boolean,
        requireCommentsForNonTeamMemberAndNonContributors?: boolean,
        requireCommentsForNonTeamMembersOnly?: boolean}) => void;
    updateEnableShellTasksArgsSanitizing: (newVal: boolean) => void;
    updateDisableImpliedYAMLCiTrigger: (newVal: boolean) => void;
}

export class OrgSettingsService extends VssService implements IOrgSettingsService {
    private _settings: ObservableValue<OrgSettingsData>;
    private _hasNoPermission: ObservableValue<boolean>;
    private _source: ContributionSource;

    public get data(): IReadonlyObservableValue<OrgSettingsData> {
        return this._settings;
    }

    public get hasNoPermission(): IReadonlyObservableValue<boolean> {
        return this._hasNoPermission;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    public _serviceEnd(pageContext: IVssPageContext): any {
        super._serviceEnd(pageContext);
    }

    public updateBadgesArePrivate = async (newVal: boolean) => {
        // Invert the value since the setting is stored as isPublic
        await this._updateSetting(c_badgesPublic, !newVal);
    };

    public updateEnforceSettableVar = async (newVal: boolean) => {
        await this._updateSetting(c_enforceSettableVar, newVal);
    };

    public updateAuditEnforceSettableVar = async (newVal: boolean) => {
        await this._updateSetting(c_auditEnforceSettableVar, newVal);
    };

    public updateDisableInBoxTasksVar = async (newVal: boolean) => {
        await this._updateSetting(c_disableInBoxTasksVar, newVal);
    };

    public updateJobAuthScope = async (newVal: boolean) => {
        await this._updateSetting(c_enforceJobAuthScope, newVal);
    };

    public updateJobAuthScopeForReleases = async (newVal: boolean) => {
        await this._updateSetting(c_enforceJobAuthScopeForReleases, newVal);
    };

    public updateEnforceReferencedRepoScopedToken = async (newVal: boolean) => {
        await this._updateSetting(c_enforceReferencedRepoScopedToken, newVal);
    };

    public updateDisableMarketplaceTasksVar = async (newVal: boolean) => {
        await this._updateSetting(c_disableMarketplaceTasksVar, newVal);
    };

    public updateDisableNode6TasksVar = async (newVal: boolean) => {
        await this._updateSetting(c_disableNode6Tasksvar, newVal);
    };

    public updateDisableStageChooser = async (newVal: boolean) => {
        await this._updateSetting(c_disableStageChooser, newVal);
    };

    public updateDisableClassicPipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(c_disableClassicPipelineCreation, newVal);
    };

    public updateDisableClassicBuildPipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(c_disableClassicBuildPipelineCreation, newVal);
    };

    public updateDisableClassicReleasePipelineCreation = async (newVal: boolean) => {
        await this._updateSetting(c_disableClassicReleasePipelineCreation, newVal);
    };
    
    public updatePipelineTriggerSettings = async (settings: {
        buildsEnabledForForks?: boolean;
        enforceJobAuthScopeForForks?: boolean;
        enforceNoAccessToSecretsFromForks?: boolean;
        forkProtectionEnabled?: boolean;
        isCommentRequiredForPullRequest?: boolean;
        requireCommentsForNonTeamMemberAndNonContributors?: boolean;
        requireCommentsForNonTeamMembersOnly?: boolean;
    }) => {
        const {
            forkProtectionEnabled,
            buildsEnabledForForks,
            enforceJobAuthScopeForForks,
            enforceNoAccessToSecretsFromForks,
            isCommentRequiredForPullRequest,
            requireCommentsForNonTeamMembersOnly,
            requireCommentsForNonTeamMemberAndNonContributors
        } = settings;
        var updates : { [key: string]: string } = {};

        if (forkProtectionEnabled !== undefined) {
            updates[c_forkProtectionEnabled] = forkProtectionEnabled.toString();
        }

        if (buildsEnabledForForks !== undefined) {
            updates[c_buildsEnabledForForks] = buildsEnabledForForks.toString();
        }

        if (enforceJobAuthScopeForForks !== undefined) {
            updates[c_enforceJobAuthScopeForForks] = enforceJobAuthScopeForForks.toString();
        }

        if (enforceNoAccessToSecretsFromForks !== undefined) {
            updates[c_enforceNoAccessToSecretsFromForks] = enforceNoAccessToSecretsFromForks.toString();
        }

        if (isCommentRequiredForPullRequest !== undefined) {
            updates[c_isCommentRequiredForPullRequest] = isCommentRequiredForPullRequest.toString();
        }

        if (requireCommentsForNonTeamMembersOnly !== undefined) {
            updates[c_requireCommentsForNonTeamMembersOnly] = requireCommentsForNonTeamMembersOnly.toString();
        }

        if (requireCommentsForNonTeamMemberAndNonContributors !== undefined) {
            updates[c_requireCommentsForNonTeamMemberAndNonContributors] = requireCommentsForNonTeamMemberAndNonContributors.toString();
        }

        await this._updateSettings(updates);
    };

    public updateEnableShellTasksArgsSanitizing = async (newVal: boolean) => {
        await this._updateSetting(c_enableShellTasksArgsSanitizing, newVal);
    };

    public updateDisableImpliedYAMLCiTrigger = async (newVal: boolean) => {
        await this._updateSetting(c_disableImpliedYAMLCiTrigger, newVal);
    };

    private _updateSetting = async (key: string, newVal: boolean) => {
        const routeData: { [key: string]: string } = {
            [key]: newVal.toString()
        };

        await this._update(routeData);
    };

    private _updateSettings = async (updates: { [key: string]: string }) => {
        await this._update(updates);
    };

    private async _update(routeData: { [key: string]: string } | undefined) {
        const data = await this._source.getDataAsync<OrgSettingsData | string>(
            "ms.vss-build-web.pipelines-org-settings-data-provider",
            routeData,
            true
        );

        if (data && typeof data === "string" && data === "AccessDeniedException") {
            this._hasNoPermission.value = true;
            return;
        }

        if (!!data) {
            this._settings.value = data as OrgSettingsData;
        }
    }

    public refreshData() {}

    private async _initialize() {
        this._source = new ContributionSource({
            pageContext: this.pageContext
        });

        this._settings = new ObservableValue<OrgSettingsData>({} as OrgSettingsData);
        this._hasNoPermission = new ObservableValue<boolean>(false);

        await this._update(undefined);
    }
}

const c_badgesPublic: string = "badgesArePublic";
const c_enforceSettableVar: string = "enforceSettableVar";
const c_auditEnforceSettableVar: string = "auditEnforceSettableVar";
const c_enforceJobAuthScope: string = "enforceJobAuthScope";
const c_enforceJobAuthScopeForReleases: string = "enforceJobAuthScopeForReleases";
const c_disableInBoxTasksVar: string = "disableInBoxTasksVar";
const c_disableMarketplaceTasksVar: string = "disableMarketplaceTasksVar";
const c_disableNode6Tasksvar: string = "disableNode6Tasksvar";
const c_enforceReferencedRepoScopedToken: string = "enforceReferencedRepoScopedToken";
const c_disableStageChooser: string = "disableStageChooser";
const c_disableClassicPipelineCreation: string = "disableClassicPipelineCreation";
const c_disableClassicBuildPipelineCreation: string = "disableClassicBuildPipelineCreation";
const c_disableClassicReleasePipelineCreation: string = "disableClassicReleasePipelineCreation";
const c_forkProtectionEnabled: string = "forkProtectionEnabled";
const c_buildsEnabledForForks: string = "buildsEnabledForForks";
const c_enforceJobAuthScopeForForks: string = "enforceJobAuthScopeForForks";
const c_enforceNoAccessToSecretsFromForks: string = "enforceNoAccessToSecretsFromForks";
const c_isCommentRequiredForPullRequest: string = "isCommentRequiredForPullRequest";
const c_requireCommentsForNonTeamMembersOnly: string = "requireCommentsForNonTeamMembersOnly";
const c_requireCommentsForNonTeamMemberAndNonContributors: string = "requireCommentsForNonTeamMemberAndNonContributors";
const c_enableShellTasksArgsSanitizing: string = "enableShellTasksArgsSanitizing";
const c_disableImpliedYAMLCiTrigger: string = "disableImpliedYAMLCiTrigger";

Services.add("IOrgSettingsService", { serviceFactory: OrgSettingsService });
